import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DataTable from "examples/Tables/DataTable";
import axios from "axios";
import API_ENDPOINTS from "config/api";

export default function AsientosContables() {
  const [asientos, setAsientos] = useState([]);
  const [filteredAsientos, setFilteredAsientos] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [viewMode, setViewMode] = useState(false);

  const [currentAsiento, setCurrentAsiento] = useState({
    numero: "",
    fecha: new Date().toISOString().split("T")[0],
    concepto: "",
    referencia: "",
    tipo: "diario", // diario, ajuste, cierre
    detalles: [],
  });

  const [nuevaLinea, setNuevaLinea] = useState({
    cuenta_id: "",
    cuenta_codigo: "",
    cuenta_nombre: "",
    debe: 0,
    haber: 0,
    descripcion: "",
  });

  const [cuentasDisponibles, setCuentasDisponibles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cargar datos desde la API
  const fetchData = async () => {
    setLoading(true);
    try {
      const [asientosRes, cuentasRes] = await Promise.all([
        axios.get(API_ENDPOINTS.CONTABILIDAD.ASIENTOS),
        axios.get(API_ENDPOINTS.CONTABILIDAD.CUENTAS),
      ]);
      setAsientos(asientosRes.data);
      setFilteredAsientos(asientosRes.data);
      setCuentasDisponibles(cuentasRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      // Fallback a datos estáticos si la API falla
      setCuentasDisponibles([
        { id: 1, codigo: "1101", nombre: "Caja" },
        { id: 2, codigo: "1102", nombre: "Bancos" },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filtrar asientos
  useEffect(() => {
    let result = asientos;

    if (searchTerm) {
      result = result.filter(
        (asiento) =>
          asiento.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
          asiento.concepto.toLowerCase().includes(searchTerm.toLowerCase()) ||
          asiento.referencia.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredAsientos(result);
  }, [searchTerm, asientos]);

  const handleOpenDialog = (asiento = null, view = false) => {
    if (asiento) {
      setCurrentAsiento(asiento);
      setViewMode(view);
    } else {
      const ultimoNumero =
        asientos.length > 0 ? parseInt(asientos[asientos.length - 1].numero.split("-")[1]) : 0;
      setCurrentAsiento({
        numero: `A-${String(ultimoNumero + 1).padStart(3, "0")}`,
        fecha: new Date().toISOString().split("T")[0],
        concepto: "",
        referencia: "",
        tipo: "diario",
        detalles: [],
      });
      setViewMode(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentAsiento({
      numero: "",
      fecha: new Date().toISOString().split("T")[0],
      concepto: "",
      referencia: "",
      tipo: "diario",
      detalles: [],
    });
    setNuevaLinea({
      cuenta_id: "",
      cuenta_codigo: "",
      cuenta_nombre: "",
      debe: 0,
      haber: 0,
      descripcion: "",
    });
  };

  const handleSelectCuenta = (cuentaId) => {
    const cuenta = cuentasDisponibles.find((c) => c.id === cuentaId);
    if (cuenta) {
      setNuevaLinea({
        ...nuevaLinea,
        cuenta_id: cuenta.id,
        cuenta_codigo: cuenta.codigo,
        cuenta_nombre: cuenta.nombre,
      });
    }
  };

  const handleAddLinea = () => {
    if (!nuevaLinea.cuenta_id || (nuevaLinea.debe === 0 && nuevaLinea.haber === 0)) {
      alert("Debe seleccionar una cuenta e ingresar un monto en Debe o Haber");
      return;
    }

    setCurrentAsiento({
      ...currentAsiento,
      detalles: [...currentAsiento.detalles, { ...nuevaLinea }],
    });

    setNuevaLinea({
      cuenta_id: "",
      cuenta_codigo: "",
      cuenta_nombre: "",
      debe: 0,
      haber: 0,
      descripcion: "",
    });
  };

  const handleRemoveLinea = (index) => {
    const nuevosDetalles = currentAsiento.detalles.filter((_, i) => i !== index);
    setCurrentAsiento({
      ...currentAsiento,
      detalles: nuevosDetalles,
    });
  };

  const calcularTotales = () => {
    const totalDebe = currentAsiento.detalles.reduce(
      (sum, detalle) => sum + parseFloat(detalle.debe || 0),
      0
    );
    const totalHaber = currentAsiento.detalles.reduce(
      (sum, detalle) => sum + parseFloat(detalle.haber || 0),
      0
    );
    const diferencia = totalDebe - totalHaber;

    return { totalDebe, totalHaber, diferencia };
  };

  const handleSaveAsiento = async () => {
    const { totalDebe, totalHaber, diferencia } = calcularTotales();

    if (currentAsiento.detalles.length < 2) {
      alert("Un asiento contable debe tener al menos 2 líneas (una en debe y una en haber)");
      return;
    }

    if (diferencia !== 0) {
      alert("El asiento no está balanceado. La suma del Debe debe ser igual a la suma del Haber");
      return;
    }

    if (!currentAsiento.concepto) {
      alert("Debe ingresar un concepto para el asiento");
      return;
    }

    try {
      await axios.post(API_ENDPOINTS.CONTABILIDAD.ASIENTOS, currentAsiento);
      fetchData();
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving asiento:", error);
      alert("Error al registrar el asiento.");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const columns = [
    { Header: "número", accessor: "numero", width: "10%", align: "left" },
    { Header: "fecha", accessor: "fecha", width: "12%", align: "left" },
    { Header: "concepto", accessor: "concepto", width: "30%", align: "left" },
    { Header: "tipo", accessor: "tipo", align: "center" },
    { Header: "total", accessor: "total", align: "right" },
    { Header: "estado", accessor: "estado", align: "center" },
    { Header: "acciones", accessor: "acciones", align: "center" },
  ];

  const rows = filteredAsientos.map((asiento) => ({
    numero: (
      <MDTypography variant="caption" fontWeight="bold">
        {asiento.numero}
      </MDTypography>
    ),
    fecha: (
      <MDTypography variant="caption" fontWeight="medium">
        {new Date(asiento.fecha).toLocaleDateString("es-MX")}
      </MDTypography>
    ),
    concepto: (
      <MDBox>
        <MDTypography variant="caption" fontWeight="medium">
          {asiento.concepto}
        </MDTypography>
        {asiento.referencia && (
          <MDTypography variant="caption" color="text" display="block">
            Ref: {asiento.referencia}
          </MDTypography>
        )}
      </MDBox>
    ),
    tipo: <Chip label={asiento.tipo} size="small" color="info" />,
    total: (
      <MDTypography variant="caption" fontWeight="bold">
        {formatCurrency(asiento.total_debe)}
      </MDTypography>
    ),
    estado: <Chip label={asiento.estado} size="small" color="success" />,
    acciones: (
      <MDBox>
        <Tooltip title="Ver detalles">
          <IconButton onClick={() => handleOpenDialog(asiento, true)} size="small" color="info">
            <Icon>visibility</Icon>
          </IconButton>
        </Tooltip>
      </MDBox>
    ),
  }));

  const { totalDebe, totalHaber, diferencia } = calcularTotales();

  return (
    <MDBox>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              {/* Encabezado */}
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <MDTypography variant="h6" fontWeight="medium">
                  Asientos Contables
                </MDTypography>
                <MDButton
                  variant="gradient"
                  color="info"
                  startIcon={<Icon>add</Icon>}
                  onClick={() => handleOpenDialog()}
                >
                  Nuevo Asiento
                </MDButton>
              </MDBox>

              <Alert severity="info" sx={{ mb: 3 }}>
                Los asientos contables registran todas las operaciones siguiendo el principio de
                partida doble: cada transacción debe tener al menos un cargo (debe) y un abono
                (haber), y la suma de ambos debe ser igual.
              </Alert>

              {/* Búsqueda */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={6}>
                  <MDInput
                    fullWidth
                    label="Buscar asiento"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <Icon>search</Icon>,
                    }}
                  />
                </Grid>
              </Grid>

              {/* Tabla de asientos */}
              <DataTable
                table={{ columns, rows }}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                noEndBorder
              />
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog para crear/ver asiento */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>{viewMode ? "Detalle del Asiento" : "Nuevo Asiento Contable"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Número de Asiento"
                value={currentAsiento.numero}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Fecha"
                value={currentAsiento.fecha}
                onChange={(e) => setCurrentAsiento({ ...currentAsiento, fecha: e.target.value })}
                InputLabelProps={{ shrink: true }}
                disabled={viewMode}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Tipo de Asiento"
                value={currentAsiento.tipo}
                onChange={(e) => setCurrentAsiento({ ...currentAsiento, tipo: e.target.value })}
                disabled={viewMode}
              >
                <MenuItem value="diario">Diario</MenuItem>
                <MenuItem value="ajuste">Ajuste</MenuItem>
                <MenuItem value="cierre">Cierre</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Referencia"
                value={currentAsiento.referencia}
                onChange={(e) =>
                  setCurrentAsiento({ ...currentAsiento, referencia: e.target.value })
                }
                disabled={viewMode}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Concepto"
                value={currentAsiento.concepto}
                onChange={(e) => setCurrentAsiento({ ...currentAsiento, concepto: e.target.value })}
                multiline
                rows={2}
                disabled={viewMode}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Movimientos del Asiento
              </MDTypography>
            </Grid>

            {/* Tabla de detalles */}
            <Grid item xs={12}>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Código</TableCell>
                      <TableCell>Cuenta</TableCell>
                      <TableCell>Descripción</TableCell>
                      <TableCell align="right">Debe</TableCell>
                      <TableCell align="right">Haber</TableCell>
                      {!viewMode && <TableCell align="center">Acciones</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentAsiento.detalles.map((detalle, index) => (
                      <TableRow key={index}>
                        <TableCell>{detalle.cuenta_codigo}</TableCell>
                        <TableCell>{detalle.cuenta_nombre}</TableCell>
                        <TableCell>{detalle.descripcion}</TableCell>
                        <TableCell align="right">
                          {detalle.debe > 0 ? formatCurrency(detalle.debe) : "-"}
                        </TableCell>
                        <TableCell align="right">
                          {detalle.haber > 0 ? formatCurrency(detalle.haber) : "-"}
                        </TableCell>
                        {!viewMode && (
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveLinea(index)}
                            >
                              <Icon>delete</Icon>
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {/* Fila de totales */}
                    <TableRow>
                      <TableCell colSpan={3} align="right">
                        <strong>TOTALES:</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatCurrency(totalDebe)}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{formatCurrency(totalHaber)}</strong>
                      </TableCell>
                      {!viewMode && <TableCell />}
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {diferencia !== 0 && !viewMode && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Diferencia: {formatCurrency(Math.abs(diferencia))} - El asiento no está balanceado
                </Alert>
              )}
              {diferencia === 0 && currentAsiento.detalles.length >= 2 && !viewMode && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  ✓ El asiento está balanceado correctamente
                </Alert>
              )}
            </Grid>

            {/* Agregar nueva línea */}
            {!viewMode && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <MDTypography variant="subtitle2" fontWeight="medium" mb={2}>
                    Agregar Movimiento
                  </MDTypography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Cuenta"
                    value={nuevaLinea.cuenta_id}
                    onChange={(e) => handleSelectCuenta(e.target.value)}
                  >
                    {cuentasDisponibles.map((cuenta) => (
                      <MenuItem key={cuenta.id} value={cuenta.id}>
                        {cuenta.codigo} - {cuenta.nombre}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Debe"
                    type="number"
                    value={nuevaLinea.debe}
                    onChange={(e) =>
                      setNuevaLinea({ ...nuevaLinea, debe: parseFloat(e.target.value) || 0 })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Haber"
                    type="number"
                    value={nuevaLinea.haber}
                    onChange={(e) =>
                      setNuevaLinea({ ...nuevaLinea, haber: parseFloat(e.target.value) || 0 })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDButton
                    variant="gradient"
                    color="success"
                    fullWidth
                    onClick={handleAddLinea}
                    sx={{ height: "100%" }}
                  >
                    <Icon>add</Icon>&nbsp;Agregar
                  </MDButton>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descripción del movimiento"
                    value={nuevaLinea.descripcion}
                    onChange={(e) => setNuevaLinea({ ...nuevaLinea, descripcion: e.target.value })}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseDialog} color="secondary">
            {viewMode ? "Cerrar" : "Cancelar"}
          </MDButton>
          {!viewMode && (
            <MDButton onClick={handleSaveAsiento} variant="gradient" color="info">
              Registrar Asiento
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
